import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Check,
  Clock3,
  CloudUpload,
  Eye,
  GitBranch,
  Hammer,
  Loader2,
  Minus,
  Package,
  Rocket,
  ScanSearch,
  X
} from "lucide-react";
import {
  buildDeploymentPipelineNodes,
  type DeploymentPipelineNode,
  type DeploymentPipelineStageEvent,
  type DeploymentPipelineStageId,
  type DeploymentPipelineStageStatus
} from "@/lib/deploymentPipeline";
import { cn } from "@/lib/utils";

const stageIcons: Record<DeploymentPipelineStageId, React.ComponentType<{ className?: string }>> = {
  queued: Clock3,
  clone: GitBranch,
  detect: ScanSearch,
  install: Package,
  build: Hammer,
  artifacts: CloudUpload,
  runtime: Box,
  preview: Eye,
  live: Rocket
};

const nodeClass = (status: DeploymentPipelineStageStatus): string => {
  if (status === "success") return "border-emerald-500/45 bg-emerald-500/10 text-emerald-100";
  if (status === "failed" || status === "cancelled") {
    return "border-destructive/55 bg-destructive/10 text-destructive-foreground";
  }
  if (status === "running") return "border-amber-400/60 bg-amber-400/10 text-amber-50 shadow-[0_0_28px_-12px_rgba(251,191,36,0.8)]";
  if (status === "skipped") return "border-border/70 bg-muted/40 text-muted-foreground";
  return "border-border/55 bg-background/75 text-muted-foreground";
};

const connectorClass = (node: DeploymentPipelineNode): string =>
  node.status === "success" || node.status === "skipped"
    ? "bg-emerald-500/45"
    : node.status === "failed" || node.status === "cancelled"
      ? "bg-destructive/45"
      : node.status === "running"
        ? "bg-gradient-to-r from-amber-400/70 to-border"
        : "bg-border/70";

const StatusMark = ({ status }: { status: DeploymentPipelineStageStatus }): React.ReactElement => {
  if (status === "running") return <Loader2 className="size-3 animate-spin" aria-hidden />;
  if (status === "success") return <Check className="size-3" aria-hidden />;
  if (status === "failed" || status === "cancelled") return <X className="size-3" aria-hidden />;
  return <Minus className="size-3" aria-hidden />;
};

export const DeploymentPipelineGraph = ({
  deploymentStatus,
  events,
  onSelectStage
}: {
  deploymentStatus: string;
  events: readonly DeploymentPipelineStageEvent[];
  onSelectStage?: (stage: DeploymentPipelineStageId) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const nodes = React.useMemo(
    () => buildDeploymentPipelineNodes(deploymentStatus, events),
    [deploymentStatus, events]
  );
  const preferred =
    nodes.find((node) => node.status === "failed" || node.status === "running") ??
    [...nodes].reverse().find((node) => node.status === "success") ??
    nodes[0];
  const [selectedId, setSelectedId] = React.useState<DeploymentPipelineStageId>(preferred?.id ?? "clone");
  const selected = nodes.find((node) => node.id === selectedId) ?? preferred;

  React.useEffect(() => {
    const important = nodes.find((node) => node.status === "failed" || node.status === "running");
    if (important) setSelectedId(important.id);
  }, [nodes]);

  const selectNode = (id: DeploymentPipelineStageId): void => {
    setSelectedId(id);
    onSelectStage?.(id);
    document.getElementById("stage-logs")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="deployment-pipeline"
      aria-label={t("deployment.pipelineAria")}
      className="overflow-hidden rounded-xl border border-border/70 bg-card/50"
    >
      <div className="flex items-center justify-between gap-4 border-b border-border/60 px-4 py-3">
        <div>
          <p className="text-[0.65rem] font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            {t("deployment.buildPipelineSection")}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("deployment.pipelineLiveHint", { defaultValue: "Live deployment flow. Select a node to inspect its output." })}
          </p>
        </div>
        <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 font-mono text-[0.6rem] tracking-[0.12em] text-muted-foreground uppercase">
          {deploymentStatus}
        </span>
      </div>

      <div
        className="overflow-x-auto bg-[radial-gradient(circle_at_1px_1px,color-mix(in_oklab,var(--border)_48%,transparent)_1px,transparent_0)] bg-[size:18px_18px] px-4 py-6"
        role="list"
      >
        <div className="mx-auto flex min-w-[62rem] items-center">
          {nodes.map((node, index) => {
            const Icon = stageIcons[node.id];
            const label = t(`deployment.pipelineStages.${node.id}`, {
              defaultValue: node.id.charAt(0).toUpperCase() + node.id.slice(1)
            });
            return (
              <React.Fragment key={node.id}>
                <button
                  type="button"
                  role="listitem"
                  aria-current={selected?.id === node.id ? "step" : undefined}
                  aria-label={`${label}: ${node.status}`}
                  onClick={() => selectNode(node.id)}
                  className={cn(
                    "group relative flex w-[6.5rem] shrink-0 flex-col items-center gap-2 rounded-lg border px-2 py-3 text-center transition",
                    "hover:-translate-y-0.5 hover:border-foreground/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    nodeClass(node.status),
                    selected?.id === node.id && "ring-1 ring-foreground/30"
                  )}
                >
                  <span className="relative flex size-8 items-center justify-center rounded-md border border-current/20 bg-background/50">
                    <Icon className="size-4" aria-hidden />
                    <span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full border border-current/25 bg-background">
                      <StatusMark status={node.status} />
                    </span>
                  </span>
                  <span className="text-[0.66rem] font-semibold tracking-[0.08em] uppercase">{label}</span>
                </button>
                {index < nodes.length - 1 ? (
                  <span className={cn("h-px min-w-5 flex-1", connectorClass(node))} aria-hidden />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {selected ? (
        <div className="flex min-h-11 items-center gap-2 border-t border-border/60 bg-background/35 px-4 py-2 text-xs">
          <span className={cn("flex size-5 items-center justify-center rounded-full border", nodeClass(selected.status))}>
            <StatusMark status={selected.status} />
          </span>
          <span className="font-medium text-foreground">
            {t(`deployment.pipelineStages.${selected.id}`, {
              defaultValue: selected.id.charAt(0).toUpperCase() + selected.id.slice(1)
            })}
          </span>
          <span className="text-muted-foreground">
            {selected.detail ?? t(`deployment.pipelineStatus.${selected.status}`, { defaultValue: selected.status })}
          </span>
        </div>
      ) : null}
    </section>
  );
};
